import React, {useEffect, useState} from "react";
import Box from '@mui/material/Box';
import DialogTitle from '@mui/material/DialogTitle';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CheckOutIcon from '@mui/icons-material/ShoppingCartCheckoutOutlined';
import { Select, MenuItem, ButtonGroup, FormGroup, FormControlLabel, FormControl, InputLabel, Typography } from '@mui/material';
import Cookies from 'js-cookie'; // Import Cookies
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import { calculateDeliveryPrice, calculateDistance } from '../utils/deliveryPricing';


import { makeStyles } from '@material-ui/core/styles';

import EmailIcon from '@material-ui/icons/Email';
import PersonIcon from '@material-ui/icons/Person';
import PhoneIcon from '@material-ui/icons/Phone';
import LocationOnIcon from '@material-ui/icons/LocationOn';

import axios from 'axios';

import CartElement from "./CartElements";
import PlaceAutocomplete from './PlaceAutocomplete';


const useStyles = makeStyles((theme) => ({
  textField: {
    backgroundColor: '#f5f5f5',  // Grey background
    borderRadius: 40,             // Rounded corners
    margin: theme.spacing(1),    // Margin
  },
  outlinedInput: {
    border: "none",              // Remove border
    "&:hover": {
      border: "none"             // Remove border on hover
    },
  },
  icon: {
    marginRight: theme.spacing(1),
  }
}));


export default function CheckOut(props){

	const classes = useStyles();


	const [isPlanningForLater, setIsPlanningForLater] = useState(false);
	const [openingHours, setOpeningHours] = useState({});
	const [availableHours, setAvailableHours] = useState([]);
	const [tempClosureDates, setTempClosureDates] = useState([]);

	const [submitted, setSubmitted] = useState("");
	const [address, setAddress] = useState("");
	const [postNum, setPostNum] = useState("");
	const [email, setEmail] = useState("");
	const [telf, setTelf] = useState("");
	const [fullName, setFullName] = useState("");
	const [comments, setComments] = useState("");
	const delivery = props.delivery;


  const [selectedDay, setSelectedDay] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  // In CheckOut component:
	const [isAddressValid, setIsAddressValid] = useState(false); // Assume true by default
	const [addressError, setAddressError] = useState("");
	const [deliveryDistance, setDeliveryDistance] = useState(null);
	const [deliveryPricing, setDeliveryPricing] = useState(null);
	const [customerLocation, setCustomerLocation] = useState(null);
	const [isPricingCalculating, setIsPricingCalculating] = useState(false);

	// Handle distance calculation callback from PlaceAutocomplete
	const handleDistanceCalculated = (distance, pricing, location) => {
		setDeliveryDistance(distance);
		setDeliveryPricing(pricing);
		setCustomerLocation(location);
		setIsPricingCalculating(false);
	};

	// Load saved data from cookies and calculate delivery price on modal open
	useEffect(() => {
		const userInfo = {
			address: Cookies.get('address'),
			postNum: Cookies.get('postNum'),
			email: Cookies.get('email'),
			telf: Cookies.get('telf'),
			fullName: Cookies.get('fullName'),
			comments: Cookies.get('comments'),
			customerLocation: Cookies.get('customerLocation')
		};

		// Restore form data
		if (userInfo.address) setAddress(userInfo.address);
		if (userInfo.postNum) setPostNum(userInfo.postNum);
		if (userInfo.email) setEmail(userInfo.email);
		if (userInfo.telf) setTelf(userInfo.telf);
		if (userInfo.fullName) setFullName(userInfo.fullName);
		if (userInfo.comments) setComments(userInfo.comments);

		// Calculate delivery price if we have saved coordinates
		if (userInfo.customerLocation && userInfo.address && delivery) {
			try {
				const location = JSON.parse(userInfo.customerLocation);
				
				// Validate coordinates
				if (!location.lat || !location.lng || isNaN(location.lat) || isNaN(location.lng)) {
					throw new Error('Invalid saved coordinates');
				}
				
				setCustomerLocation(location);
				
				// Check if Wolt Drive is enabled
				if (props.isWoltDriveEnabled) {
					console.log('DEBUG: Loading from cookies - validating Wolt route');
					setIsPricingCalculating(true);
					
					// Step 1: Validate route with Wolt
					axios.post(`${process.env.REACT_APP_API_ADDRESS}/get-wolt-shipment-promise`, {
						restaurantId: props.restaurantId,
						customerAddress: {
							address: userInfo.address,
							lat: location.lat,
							lng: location.lng
						}
					})
					.then(response => {
						const { route_available, error } = response.data;
						
						if (error || !route_available) {
							setDeliveryPricing({ price: 0, withinLimit: false, error: error || 'Wolt kan ikke levere til denne adresse' });
							setAddressError(error || 'Wolt kan ikke levere til denne adresse');
							setIsAddressValid(false);
							setIsPricingCalculating(false);
							return;
						}
						
						// Step 2: Route valid - calculate our own price
						if (!props.restaurantCoordinates || !props.deliveryPricingConfig) {
							setDeliveryPricing({ price: 0, withinLimit: false, error: 'Leveringspris konfiguration mangler' });
							setAddressError('Leveringspris konfiguration mangler');
							setIsAddressValid(false);
							setIsPricingCalculating(false);
							return;
						}
						
						calculateDistance(props.restaurantCoordinates, location)
							.then(distance => {
								setDeliveryDistance(distance);
								// Pass isWoltDelivery=true to skip max distance check
								const pricing = calculateDeliveryPrice(distance, props.deliveryPricingConfig, true);
								
								const woltPricing = {
									...pricing,
									is_wolt_delivery: true
								};
								
								setDeliveryPricing(woltPricing);
								setIsAddressValid(true);
								setIsPricingCalculating(false);
								
								// Update parent component with delivery data
								props.setCheckOutData({
									deliveryDistance: distance,
									deliveryPricing: woltPricing,
									customerLocation: location,
									_skipAutoPayment: true
								});
							})
							.catch(error => {
								console.error('Distance calculation failed (from cookies):', error);
								setDeliveryPricing({ price: 0, withinLimit: false, error: 'Kunne ikke beregne leveringsafstand' });
								setAddressError('Kunne ikke beregne leveringsafstand');
								setIsAddressValid(false);
								setIsPricingCalculating(false);
							});
					})
					.catch(error => {
						console.error('Wolt route validation failed (from cookies):', error);
						setDeliveryPricing({ 
							price: 0, 
							withinLimit: false, 
							error: 'Kunne ikke validere rute med Wolt. Venligst indtast din adresse igen.' 
						});
						setAddressError('Kunne ikke validere rute med Wolt. Venligst indtast din adresse igen.');
						setIsAddressValid(false);
						setIsPricingCalculating(false);
					});
					
				} else if (props.restaurantCoordinates && props.deliveryPricingConfig) {
					// Standard distance-based pricing
					console.log('DEBUG: Loading from cookies - calculating standard pricing');
					setIsPricingCalculating(true);
					
					const calculatePriceAsync = async () => {
						try {
							const distance = await calculateDistance(
								props.restaurantCoordinates,
								location
							);
							const pricing = calculateDeliveryPrice(distance, props.deliveryPricingConfig);
							
							setDeliveryDistance(distance);
							setDeliveryPricing(pricing);
							setIsAddressValid(true);
						} catch (error) {
							console.error('Error calculating delivery price from saved data:', error);
							setIsAddressValid(false);
						} finally {
							setIsPricingCalculating(false);
						}
					};
					
					calculatePriceAsync();
				}
			} catch (error) {
				console.error('Error parsing saved customer location:', error);
				setIsAddressValid(false);
			}
		}
	}, [props.restaurantCoordinates, props.deliveryPricingConfig, props.isWoltDriveEnabled, props.restaurantId, delivery]);

	// Save form data to cookies whenever they change
	useEffect(() => {
		if (address) Cookies.set('address', address, { expires: 7, secure: true, sameSite: 'None', path: '/' });
		if (postNum) Cookies.set('postNum', postNum, { expires: 7, secure: true, sameSite: 'None', path: '/' });
		if (email) Cookies.set('email', email, { expires: 7, secure: true, sameSite: 'None', path: '/' });
		if (telf) Cookies.set('telf', telf, { expires: 7, secure: true, sameSite: 'None', path: '/' });
		if (fullName) Cookies.set('fullName', fullName, { expires: 7, secure: true, sameSite: 'None', path: '/' });
		if (comments) Cookies.set('comments', comments, { expires: 7, secure: true, sameSite: 'None', path: '/' });
		if (customerLocation) Cookies.set('customerLocation', JSON.stringify(customerLocation), { expires: 7, secure: true, sameSite: 'None', path: '/' });
	}, [address, postNum, email, telf, fullName, comments, customerLocation]);


	// Utility function to generate an array of days for the dropdown
	const getDays = () => {
	  const weekdays = ["søndag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag"];
	  let days = [];

	  for (let i = 0; i < 7; i++) {
	    const day = new Date();
	    day.setDate(day.getDate() + i);
	    const dayOfWeek = weekdays[day.getDay()];
	    const dayString = day.toISOString().split('T')[0];

	    // Safely handle tempClosureDates being null or undefined
	    const isTempClosed = Array.isArray(tempClosureDates) && tempClosureDates.length > 0 &&
	      dayString >= tempClosureDates[0] && dayString <= tempClosureDates[1];

	    // Check if the day is marked as closed
	    const isClosed = (openingHours[dayOfWeek] && openingHours[dayOfWeek].isOpen === false) || isTempClosed;

	    const dayLabel = i === 0 ? "I dag" : i === 1 ? "I morgen" : dayOfWeek;
	    
	    days.push({
	      label: isClosed ? `${dayLabel} (Lukket)` : dayLabel,
	      value: day.toISOString().split('T')[0], // Keeping the date value in YYYY-MM-DD format
	      isClosed: isClosed
	    });
	  }

	  console.log(days);
	  return days;
	};


  // Assuming the hours are from 8:00 to 20:00, adjust according to your needs
  const hours = Array.from({ length: 13 }, (_, i) => 8 + i);


	const generateTimeOptions = (startHour, startMin, endHour, endMin, includeCurrentDay = false) => {
			console.log("STARTMIN: ", startMin);
	    const times = [];
	    let currentHour = new Date().getHours();
	    let currentMinute = new Date().getMinutes();

	    // Adjust for current day by setting start time one hour ahead
	    if (includeCurrentDay) {
	        currentMinute += 60; // Add 60 minutes to the current time
	        if (currentMinute >= 60) {
	            currentHour++;
	            currentMinute %= 60; // Adjust minutes after incrementing hour
	        }
	        // Round up to the next 5-minute interval
	        if (currentMinute % 5 !== 0) {
	            currentMinute += 5 - (currentMinute % 5);
	        }
	        if (currentMinute === 60) {
	            currentHour++;
	            currentMinute = 0;
	        }
	    }

	    // Start from the later of the opening time or the current time adjusted for today
	    let startCompHour = includeCurrentDay ? Math.max(currentHour, startHour) : startHour;
	    let startCompMinute = currentHour > startHour && includeCurrentDay ? currentMinute : startMin;

	    // Adjust starting minute for the first slot based on the current time
	    if (includeCurrentDay && startCompHour === currentHour) {
	        startCompMinute = currentMinute;
	    }

	    let hour = startCompHour;
	    let minute = startCompMinute;

	    while (true) {
	        if (hour > endHour || (hour === endHour && minute > endMin)) break; // Ensure within closing time
	        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
	        times.push(timeString);

	        minute += 5;
	        if (minute >= 60) {
	            minute = 0;
	            hour++;
	        }
	    }

	    return times.filter(time => {
	        const [hrs, mins] = time.split(':').map(Number);
	        return hrs > startHour || (hrs === startHour && mins >= startMin) // After opening
	            && (hrs < endHour || (hrs === endHour && mins <= endMin)); // Before closing
	    });
	};


  // Adjusted getHoursForDay to consider if selectedDay is today
  const getHoursForDay = (day) => {
    const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
    const isToday = day === today;
    
    const dayOfWeek = new Date(day).getDay();
    const weekdays = ["søndag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag"];
    const dayName = weekdays[dayOfWeek];
    const hoursInfo = openingHours[dayName]; // Assuming openingHours is structured appropriately

    if (!hoursInfo  || !hoursInfo.isOpen || (tempClosureDates && tempClosureDates.includes(day))) return [];

	  let availableTimes = [];

	  hoursInfo.intervals.forEach(interval => {
	    const [startHour, startMin] = interval.open.split(':').map(Number);
	    const [endHour, endMin] = interval.close.split(':').map(Number);

	    const intervalTimes = generateTimeOptions(startHour, startMin, endHour, endMin, isToday);
	    availableTimes = availableTimes.concat(intervalTimes);
	  });

    return availableTimes;
  };


	useEffect(() => {
	  if (selectedDay) {
	    const hours = getHoursForDay(selectedDay);
	    setAvailableHours(hours); // This updates the availableHours state
	  } else {
	    setAvailableHours([]); // Resets available hours if no day is selected
	  }

	  console.log("OpeningHours: ", openingHours);
	}, [selectedDay, openingHours, , tempClosureDates]);


	useEffect(() => {
	  fetch(`${process.env.REACT_APP_OPENING_HOURS_API}/GetPreOrderHours/${props.restaurantId}`, {
	    method: "GET",
	  })
	    .then(response => response.json())
	    .then(data => {
	    	//console.log(data);
	    	//console.log("TempClosure" + data.temporarilyClosed);
	      setOpeningHours(data.hoursData.scheduled_opening_hours);
	      setTempClosureDates(data.temporarilyClosed);
	    });
	}, [props.restaurantId]);


	// Inside your component
	useEffect(() => {
	  // Automatically set to 'Planlæg til senere' if props.isOpen is false
	  if (!props.isOpen) {
	    setIsPlanningForLater(true);
	  }
	}, [props.isOpen]);


	useEffect(() => {
	  console.log('DEBUG: CheckOut useEffect - Props received:', {
	    delivery,
	    restaurantCoordinates: props.restaurantCoordinates,
	    deliveryPricingConfig: props.deliveryPricingConfig,
	    isOpen: props.isOpen
	  });
	  
	  // Load user info from cookies when the component mounts
	  const userInfo = {
	    address: Cookies.get('address'),
	    postNum: Cookies.get('postNum'),
	    email: Cookies.get('email'),
	    telf: Cookies.get('telf'),
	    fullName: Cookies.get('fullName'),
	    customerLat: Cookies.get('customerLat'),
	    customerLng: Cookies.get('customerLng')
	  };

	  console.log('DEBUG: User info from cookies:', userInfo);

	  if (userInfo.address) setAddress(userInfo.address);
	  if (userInfo.postNum) setPostNum(userInfo.postNum);
	  if (userInfo.email) setEmail(userInfo.email);
	  if (userInfo.telf) setTelf(userInfo.telf);
	  if (userInfo.fullName) setFullName(userInfo.fullName);

	  // Check if we have stored coordinates and delivery is selected
	  const hasStoredCoordinates = userInfo.customerLat && userInfo.customerLng && 
	                               !isNaN(parseFloat(userInfo.customerLat)) && 
	                               !isNaN(parseFloat(userInfo.customerLng));

	  if (delivery && hasStoredCoordinates && userInfo.address) {
	    // We have coordinates and address, validate and calculate delivery price
	    console.log('DEBUG: Starting delivery calculation on dialog open', {
	      delivery,
	      hasStoredCoordinates,
	      address: userInfo.address,
	      restaurantCoordinates: props.restaurantCoordinates,
	      deliveryPricingConfig: props.deliveryPricingConfig
	    });
	    
	    getGeocode({ address: userInfo.address })
	      .then(results => {
	        const addressComponents = results[0].address_components;
	        const streetNumber = addressComponents.find(comp => comp.types.includes("street_number"));
	        const route = addressComponents.find(comp => comp.types.includes("route"));

	        if (streetNumber && route) {
	          setIsAddressValid(true);
	          setAddressError("");
	          
	          // Start delivery calculation immediately
	          const customerCoords = {
	            lat: parseFloat(userInfo.customerLat),
	            lng: parseFloat(userInfo.customerLng)
	          };
	          setCustomerLocation(customerCoords);
	          
	          console.log('DEBUG: About to calculate distance', {
	            restaurantCoords: props.restaurantCoordinates,
	            customerCoords,
	            pricingConfig: props.deliveryPricingConfig,
	            isWoltDriveEnabled: props.isWoltDriveEnabled
	          });
	          
	          // Check if Wolt Drive is enabled - need to validate route first
	          if (props.isWoltDriveEnabled) {
	            setIsPricingCalculating(true);
	            axios.post(`${process.env.REACT_APP_API_ADDRESS}/get-wolt-shipment-promise`, {
	              restaurantId: props.restaurantId,
	              customerAddress: {
	                address: userInfo.address,
	                lat: customerCoords.lat,
	                lng: customerCoords.lng
	              }
	            })
	            .then(response => {
	              const { route_available, error } = response.data;
	              if (error || !route_available) {
	                setDeliveryPricing({ price: 0, withinLimit: false, error: error || 'Vi kan desværre ikke levere til denne adresse' });
	                setAddressError(error || 'Vi kan desværre ikke levere til denne adresse');
	                setIsAddressValid(false);
	                setIsPricingCalculating(false);
	                return;
	              }
	              
	              // Route valid - calculate our price
	              calculateDistance(props.restaurantCoordinates, customerCoords)
	                .then(distance => {
	                  console.log('DEBUG: Distance calculated:', distance);
	                  setDeliveryDistance(distance);
	                  const pricing = calculateDeliveryPrice(distance, props.deliveryPricingConfig, true);
	                  console.log('DEBUG: Pricing calculated:', pricing);
	                  const woltPricing = { ...pricing, is_wolt_delivery: true };
	                  setDeliveryPricing(woltPricing);
	                  setIsPricingCalculating(false);
	                  
	                  // Update parent component with calculated data
	                  props.setCheckOutData({
	                    address: userInfo.address,
	                    postNum: userInfo.postNum || postNum,
	                    email: userInfo.email || email,
	                    telf: userInfo.telf || telf,
	                    fullName: userInfo.fullName || fullName,
	                    delivery: delivery.toString(),
	                    isPlanningForLater: isPlanningForLater,
	                    selectedDay: selectedDay,
	                    selectedTime: selectedTime,
	                    comments: comments,
	                    deliveryDistance: distance,
	                    deliveryPricing: woltPricing,
	                    customerLocation: customerCoords,
	                    _skipAutoPayment: true
	                  });
	                })
	                .catch(err => {
	                  console.error('Distance calculation failed:', err);
	                  setDeliveryPricing({ price: 0, withinLimit: false, error: 'Kunne ikke beregne leveringsafstand' });
	                  setIsPricingCalculating(false);
	                });
	            })
	            .catch(error => {
	              console.error('Wolt route validation failed:', error);
	              setDeliveryPricing({ price: 0, withinLimit: false, error: 'Kunne ikke validere rute' });
	              setIsPricingCalculating(false);
	            });
	          } else if (props.restaurantCoordinates && props.deliveryPricingConfig) {
	            // Standard distance-based delivery (not Wolt)
	            setIsPricingCalculating(true);
	            calculateDistance(props.restaurantCoordinates, customerCoords)
	              .then(distance => {
	                console.log('DEBUG: Distance calculated:', distance);
	                setDeliveryDistance(distance);
	                // Don't pass isWoltDelivery flag for self-delivery
	                const pricing = calculateDeliveryPrice(distance, props.deliveryPricingConfig);
	                console.log('DEBUG: Pricing calculated:', pricing);
	                setDeliveryPricing(pricing);
	                setIsPricingCalculating(false);
	                
	                // Update parent component with calculated data (without triggering payment)
	                props.setCheckOutData({
	                  address: userInfo.address,
	                  postNum: userInfo.postNum || postNum,
	                  email: userInfo.email || email,
	                  telf: userInfo.telf || telf,
	                  fullName: userInfo.fullName || fullName,
	                  delivery: delivery.toString(),
	                  isPlanningForLater: isPlanningForLater,
	                  selectedDay: selectedDay,
	                  selectedTime: selectedTime,
	                  comments: comments,
	                  deliveryDistance: distance,
	                  deliveryPricing: pricing,
	                  customerLocation: customerCoords,
	                  _skipAutoPayment: true  // Flag to prevent automatic payment trigger
	                });
	              })
	              .catch(error => {
	                console.error('Distance calculation failed:', error);
	                setDeliveryPricing({ price: 0, withinLimit: false, error: 'Kunne ikke beregne leveringsafstand' });
	                setIsPricingCalculating(false);
	              });
	          } else {
	            console.log('DEBUG: Missing restaurant coordinates or pricing config');
	          }
	        } else {
	          setIsAddressValid(false);
	          setAddressError("Gemte adresse er ikke komplet. Vælg venligst en ny adresse.");
	        }
	      })
	      .catch(() => {
	        setIsAddressValid(false);
	        setAddressError("Kunne ikke verificere den gemte adresse. Vælg venligst en ny adresse.");
	      });
	  } else if (delivery && !hasStoredCoordinates) {
	    // No stored coordinates, clear address field and set invalid
	    setAddress('');
	    setIsAddressValid(false);
	  } else if (userInfo.address && !delivery) {
	    // Not delivery order, just validate address normally
	    getGeocode({ address: userInfo.address })
	      .then(results => {
	        const addressComponents = results[0].address_components;
	        const streetNumber = addressComponents.find(comp => comp.types.includes("street_number"));
	        const route = addressComponents.find(comp => comp.types.includes("route"));

	        if (streetNumber && route) {
	          setIsAddressValid(true);
	          setAddressError("");
	        } else {
	          setIsAddressValid(false);
	          setAddressError("Gemte adresse er ikke komplet. Vælg venligst en ny adresse.");
	        }
	      })
	      .catch(() => {
	        setIsAddressValid(false);
	        setAddressError("Kunne ikke verificere den gemte adresse. Vælg venligst en ny adresse.");
	      });
	  } else {
	    // No address in cookie, just set invalid so user must pick a new one
	    setIsAddressValid(false);
	  }
	}, [delivery, props.restaurantCoordinates, props.deliveryPricingConfig]);


	useEffect(() => {
	  // Update cookies whenever user info changes
	  Cookies.set('address', address, {expires: 365, secure: true, sameSite: 'None', path: '/' });
	  Cookies.set('postNum', postNum, {expires: 365, secure: true, sameSite: 'None', path: '/' });
	  Cookies.set('email', email, {expires: 365, secure: true, sameSite: 'None', path: '/' });
	  Cookies.set('telf', telf, {expires: 365, secure: true, sameSite: 'None', path: '/' });
	  Cookies.set('fullName', fullName, {expires: 365, secure: true, sameSite: 'None', path: '/' });
	  
	  // Save customer coordinates if available
	  if (customerLocation && customerLocation.lat && customerLocation.lng) {
	    Cookies.set('customerLat', customerLocation.lat.toString(), {expires: 365, secure: true, sameSite: 'None', path: '/' });
	    Cookies.set('customerLng', customerLocation.lng.toString(), {expires: 365, secure: true, sameSite: 'None', path: '/' });
	  }
	}, [address, postNum, email, telf, fullName, customerLocation]);



	// Handle address selection from PlaceAutocomplete - trigger immediate calculation
	const handleAddressSelected = (selectedAddress, coordinates) => {
		console.log('DEBUG: handleAddressSelected called', {
			selectedAddress,
			coordinates,
			delivery,
			isWoltDriveEnabled: props.isWoltDriveEnabled,
			restaurantCoordinates: props.restaurantCoordinates,
			deliveryPricingConfig: props.deliveryPricingConfig
		});
		
		setAddress(selectedAddress);
		setCustomerLocation(coordinates);
		
		// Check if Wolt Drive is enabled
		if (delivery && props.isWoltDriveEnabled && coordinates) {
			console.log('DEBUG: Wolt Drive enabled - validating route with Wolt');
			setIsPricingCalculating(true);
			
			// Step 1: Call Wolt API to validate route availability
			axios.post(`${process.env.REACT_APP_API_ADDRESS}/get-wolt-shipment-promise`, {
				restaurantId: props.restaurantId,
				customerAddress: {
					address: selectedAddress,
					lat: coordinates.lat,
					lng: coordinates.lng
				}
			})
			.then(response => {
				console.log('DEBUG: Wolt route validation response:', response.data);
				const { route_available, error } = response.data;
				
				if (error || !route_available) {
					setDeliveryPricing({ price: 0, withinLimit: false, error: error || 'Wolt kan ikke levere til denne adresse' });
					setAddressError(error || 'Wolt kan ikke levere til denne adresse');
					setIsAddressValid(false);
					setIsPricingCalculating(false);
					return;
				}
				
				// Step 2: Route is valid! Now calculate OUR price using standard distance calculator
				console.log('DEBUG: Wolt route valid - calculating our own price');
				
				if (!props.restaurantCoordinates || !props.deliveryPricingConfig) {
					setDeliveryPricing({ price: 0, withinLimit: false, error: 'Leveringspris konfiguration mangler' });
					setAddressError('Leveringspris konfiguration mangler');
					setIsAddressValid(false);
					setIsPricingCalculating(false);
					return;
				}
				
				calculateDistance(props.restaurantCoordinates, coordinates)
					.then(distance => {
						console.log('DEBUG: Distance calculated:', distance);
						setDeliveryDistance(distance);
						
						// Pass isWoltDelivery=true to skip max distance check (Wolt decides availability)
						const pricing = calculateDeliveryPrice(distance, props.deliveryPricingConfig, true);
						console.log('DEBUG: Our price calculated:', pricing);
						
						// Add Wolt delivery flag
						const woltPricing = {
							...pricing,
							is_wolt_delivery: true  // Flag to indicate this should be delivered via Wolt
						};
						
						setDeliveryPricing(woltPricing);
						setIsAddressValid(true);
						setIsPricingCalculating(false);
						
						// Update parent component
						props.setCheckOutData({
							address: selectedAddress,
							postNum: postNum,
							email: email,
							telf: telf,
							fullName: fullName,
							delivery: delivery.toString(),
							isPlanningForLater:isPlanningForLater,
							selectedDay:selectedDay,
							selectedTime:selectedTime,
							comments:comments,
							deliveryDistance: distance,
							deliveryPricing: woltPricing,
							customerLocation: coordinates,
							_skipAutoPayment: true
						});
					})
					.catch(error => {
						console.error('Distance calculation failed:', error);
						setDeliveryPricing({ price: 0, withinLimit: false, error: 'Kunne ikke beregne leveringsafstand' });
						setAddressError('Kunne ikke beregne leveringsafstand');
						setIsAddressValid(false);
						setIsPricingCalculating(false);
					});
			})
			.catch(error => {
				console.error('Wolt route validation failed:', error);
				setDeliveryPricing({ 
					price: 0, 
					withinLimit: false, 
					error: 'Kunne ikke validere rute med Wolt. Prøv venligst igen.' 
				});
				setAddressError('Kunne ikke validere rute med Wolt. Prøv venligst igen.');
				setIsAddressValid(false);
				setIsPricingCalculating(false);
			});
			
		} else if (delivery && !props.isWoltDriveEnabled && props.restaurantCoordinates && props.deliveryPricingConfig && coordinates) {
			// Standard distance-based pricing (restaurant self-delivery only)
			console.log('DEBUG: Starting standard distance calculation after address selection');
			setIsPricingCalculating(true);
			calculateDistance(props.restaurantCoordinates, coordinates)
				.then(distance => {
					console.log('DEBUG: Distance calculated:', distance);
					console.log('DEBUG: Address selection - distance calculated:', distance);
					setDeliveryDistance(distance);
					// Self-delivery: enforce max distance limit
					const pricing = calculateDeliveryPrice(distance, props.deliveryPricingConfig, false);
					console.log('DEBUG: Address selection - pricing calculated:', pricing);
					setDeliveryPricing(pricing);
					setIsPricingCalculating(false);
					
					// Update parent component immediately (without triggering payment)
					props.setCheckOutData({
						address: selectedAddress,
						postNum: postNum,
						email: email,
						telf: telf,
						fullName: fullName,
						delivery: delivery.toString(),
						isPlanningForLater:isPlanningForLater,
						selectedDay:selectedDay,
						selectedTime:selectedTime,
						comments:comments,
						deliveryDistance: distance,
						deliveryPricing: pricing,
						customerLocation: coordinates,
						_skipAutoPayment: true  // Flag to prevent automatic payment trigger
					});
				})
				.catch(error => {
					console.error('Distance calculation failed:', error);
					setDeliveryPricing({ price: 0, withinLimit: false, error: 'Kunne ikke beregne leveringsafstand' });
					setIsPricingCalculating(false);
				});
		} else {
			console.log('DEBUG: Address selection - conditions not met for calculation', {
				delivery,
				isWoltDriveEnabled: props.isWoltDriveEnabled,
				hasRestaurantCoords: !!props.restaurantCoordinates,
				hasPricingConfig: !!props.deliveryPricingConfig,
				hasCoordinates: !!coordinates
			});
		}
	};

	function submit() {
	  setSubmitted(true);

	  if (delivery && !isAddressValid) {
	    return;
	  }

	  // Validate delivery distance and pricing
	  if (delivery && deliveryPricing && !deliveryPricing.withinLimit) {
	    return;
	  }

	  props.setCheckOutData({
	    address: address,
	    postNum: postNum,
	    email: email,
	    telf: telf,
	    fullName: fullName,
	    delivery: delivery.toString(),
	    isPlanningForLater:isPlanningForLater,
	    selectedDay:selectedDay,
	    selectedTime:selectedTime,
	    comments:comments,
	    deliveryDistance: deliveryDistance,
	    deliveryPricing: deliveryPricing,
	    customerLocation: customerLocation
	  });
	}

	return <>
		<Box component="div" sx={{width:{xs:"90%", md:"18vw"} }} alignItems="center" justifyContent="center">
			{delivery && <PlaceAutocomplete 
				glassUI={props.glassUI} 
				setAddress={setAddress} 
				theValue={address} 
				setIsAddressValid={setIsAddressValid} 
				setAddressError={setAddressError} 
				addressError={addressError}
				restaurantCoordinates={props.restaurantCoordinates}
				deliveryPricingConfig={props.deliveryPricingConfig}
				onDistanceCalculated={handleDistanceCalculated}
				onAddressSelected={handleAddressSelected}
				onPricingCalculationStart={() => setIsPricingCalculating(true)}
			/>}
			{delivery && <TextField id="standard-basic" sx={props.glassUI === "true" ? {
				marginTop: "15px",
				marginBottom: "15px",
			    input: { color: 'white' }, // Text color
			    '& label': {
			      color: 'white', // Label color
			    },
			    '& .MuiInput-underline:before': {
			      borderBottomColor: 'white', // Underline color before focus
			    },
			    '& .MuiInput-underline:after': {
			      borderBottomColor: 'white', // Underline color after focus
			    },
			    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
			      borderBottomColor: 'white', // Underline color when hovering
			    },
			    '& .MuiFormHelperText-root': {
			      color: 'white', // Helper text color
			    },
			  }:{}}  label="Post Nummer" variant="standard" onChange={e => setPostNum(e.target.value)} value={postNum} error={!postNum && submitted && delivery} fullWidth />}

			<TextField id="standard-basic" sx={props.glassUI === "true" ? {
				marginBottom: "15px",
			    input: { color: 'white' }, // Text color
			    '& label': {
			      color: 'white', // Label color
			    },
			    '& .MuiInput-underline:before': {
			      borderBottomColor: 'white', // Underline color before focus
			    },
			    '& .MuiInput-underline:after': {
			      borderBottomColor: 'white', // Underline color after focus
			    },
			    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
			      borderBottomColor: 'white', // Underline color when hovering
			    },
			    '& .MuiFormHelperText-root': {
			      color: 'white', // Helper text color
			    },
			  } : {}} label="Email" variant="standard" fullWidth onChange={e => setEmail(e.target.value)} value={email} error={!email && submitted} />

			<TextField id="standard-basic" sx={props.glassUI === "true" ? {
				marginBottom: "15px",
			    input: { color: 'white' }, // Text color
			    '& label': {
			      color: 'white', // Label color
			    },
			    '& .MuiInput-underline:before': {
			      borderBottomColor: 'white', // Underline color before focus
			    },
			    '& .MuiInput-underline:after': {
			      borderBottomColor: 'white', // Underline color after focus
			    },
			    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
			      borderBottomColor: 'white', // Underline color when hovering
			    },
			    '& .MuiFormHelperText-root': {
			      color: 'white', // Helper text color
			    },
			  } : {}}  label="Telefon Nummer" variant="standard" onChange={e => setTelf(e.target.value)} value={telf} error={!telf && submitted} fullWidth />

			<TextField id="standard-basic" sx={props.glassUI === "true" ? {
			    input: { color: 'white' }, // Text color
			    '& label': {
			      color: 'white', // Label color
			    },
			    '& .MuiInput-underline:before': {
			      borderBottomColor: 'white', // Underline color before focus
			    },
			    '& .MuiInput-underline:after': {
			      borderBottomColor: 'white', // Underline color after focus
			    },
			    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
			      borderBottomColor: 'white', // Underline color when hovering
			    },
			    '& .MuiFormHelperText-root': {
			      color: 'white', // Helper text color
			    },
			  } : {}} label="Fulde Navn" variant="standard" onChange={e => setFullName(e.target.value)} value={fullName} error={!fullName && submitted} fullWidth />



			<TextField id="standard-basic" sx={props.glassUI === "true" ? {
			    input: { color: 'white' }, // Text color
			    '& label': {
			      color: 'white', // Label color
			    },
			    '& .MuiInput-underline:before': {
			      borderBottomColor: 'white', // Underline color before focus
			    },
			    '& .MuiInput-underline:after': {
			      borderBottomColor: 'white', // Underline color after focus
			    },
			    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
			      borderBottomColor: 'white', // Underline color when hovering
			    },
			    '& .MuiFormHelperText-root': {
			      color: 'white', // Helper text color
			    },
			  } : {}} label="kommentar" variant="standard" inputProps={{maxLength: 80}} onChange={e => setComments(e.target.value)} value={comments} fullWidth />




			<ButtonGroup variant="outlined" aria-label="outlined primary button group" sx={{marginLeft: 4, marginTop: 2}}>
			  <Button 
			    onClick={() => setIsPlanningForLater(false)} 
			    disabled={!props.isOpen} // Disable this button if props.isOpen is false
			    sx={{
			      color: !isPlanningForLater ? 'black' : 'white', // Black text for selected (now), white for unselected
			      backgroundColor: !isPlanningForLater ? 'white' : 'grey', // White background for selected (now)
			      '&:hover': {
			        backgroundColor: !isPlanningForLater ? 'rgba(255, 255, 255, 0.8)' : 'rgba(128, 128, 128, 0.8)', // Lighter on hover
			      },
			    }}
			  >
			    Nu
			  </Button>
			  <Button 
			    onClick={() => setIsPlanningForLater(true)} 
			    sx={{
			      color: isPlanningForLater ? 'black' : 'white', // Black text for selected (later), white for unselected
			      backgroundColor: isPlanningForLater ? 'white' : 'grey', // White background for selected (later)
			      '&:hover': {
			        backgroundColor: isPlanningForLater ? 'rgba(255, 255, 255, 0.8)' : 'rgba(128, 128, 128, 0.8)', // Lighter on hover
			      },
			    }}
			  >
			    Planlæg til senere
			  </Button>
			</ButtonGroup>



			{isPlanningForLater && (
				<>
		      <FormControl fullWidth sx={{ marginTop: 2, marginBottom: 1 }}>
		        <InputLabel id="day-select-label"
		        sx={props.glassUI === "true" ? {
		          color: 'white',
		          '& .MuiOutlinedInput-notchedOutline': {
		            borderColor: 'white',
		          },
		          '&:hover .MuiOutlinedInput-notchedOutline': {
		            borderColor: 'white', // Border color on hover
		          },
		          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
		            borderColor: 'white', // Border color when the input is focused
		          },
		        }: null}
		        >Day</InputLabel>
						<Select
						  labelId="day-select-label"
						  id="day-select"
						  value={selectedDay}
						  label="Dag"
						  sx={props.glassUI === "true" ? {
						    color: 'white',
						    '& .MuiOutlinedInput-notchedOutline': {
						      borderColor: 'white',
						    },
						    '&:hover .MuiOutlinedInput-notchedOutline': {
						      borderColor: 'white', // Border color on hover
						    },
						    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
						      borderColor: 'white', // Border color when the input is focused
						    },
						  }: null}
						  onChange={(e) => {
						    const selected = getDays().find(day => day.value === e.target.value);
						    if (selected && selected.isClosed) {
						      alert('This day is closed. Please choose another day.');
						      return;
						    }
						    setSelectedDay(e.target.value);
						    // Update available hours based on the selected day
						    setAvailableHours(getHoursForDay(e.target.value));
						  }}
						>
						  {getDays().map((day, index) => (
						    <MenuItem key={index} value={day.value} disabled={day.isClosed}>
						      {day.label}
						    </MenuItem>
						  ))}
						</Select>
		      </FormControl>

		      <FormControl fullWidth sx={{ marginBottom: 2 }}>
		        <InputLabel id="time-select-label"
		        	sx={ props.glassUI === "true" ? {
					      color: 'white',
					      '& .MuiOutlinedInput-notchedOutline': {
					        borderColor: 'white',
					      },
					      '&:hover .MuiOutlinedInput-notchedOutline': {
					        borderColor: 'white', // Border color on hover
					      },
					      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
					        borderColor: 'white', // Border color when the input is focused
					      },
					    }: null}
					    >Time</InputLabel>
		        <Select
						  labelId="time-select-label"
						  id="time-select"
						  value={selectedTime}
						  label="Time"
						  onChange={(e) => setSelectedTime(e.target.value)}
						  disabled={!selectedDay || availableHours.length === 0}
						  sx={ props.glassUI === "true" ? {
					      color: 'white',
					      '& .MuiOutlinedInput-notchedOutline': {
					        borderColor: 'white',
					      },
					      '&:hover .MuiOutlinedInput-notchedOutline': {
					        borderColor: 'white', // Border color on hover
					      },
					      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
					        borderColor: 'white', // Border color when the input is focused
					      },
					    }: null}
						>
						  {availableHours.map((hour, index) => (
						    <MenuItem key={index} value={hour}>
						      {hour}
						    </MenuItem>
						  ))}
						</Select>
		      </FormControl>
		    </>
			)}


			{/* Display address validation error */}
			{delivery && addressError && (
				<Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
					{addressError}
				</Typography>
			)}

			{/* Display delivery pricing error (e.g., Wolt API failures) */}
			{delivery && deliveryPricing && deliveryPricing.error && (
				<Typography variant="body2" color="error" sx={{ marginTop: 1, marginBottom: 1 }}>
					{deliveryPricing.error}
				</Typography>
			)}

		</Box>
		{props.children}
		<Button
		  variant="outlined"
		  startIcon={<CheckOutIcon />}
		  onClick={submit}
		  sx={{ marginTop: "10px", width: { xs: "90%", md: "19vw" } }}
		  size="large"
		  disabled={delivery && (!isAddressValid || (deliveryPricing && !deliveryPricing.withinLimit) || isPricingCalculating)}
		>
		  {isPricingCalculating ? "Beregner pris..." : "Check Ud"}
		</Button>



	</>;
}